import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { Meta, Metas, Clientes } from 'app/interfaces';
import { Location } from '@angular/common';
import { finalize } from 'rxjs';
import { DialogService, ClienteService, MetaService } from 'app/services';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-metas-form',
  standalone: true,
  imports: [MatSelectModule, CommonModule, ReactiveFormsModule, MatButton, TranslocoModule],
  templateUrl: './metas-form.component.html',
  styleUrl: './metas-form.component.css'
})
export class MetasFormComponent implements OnInit {

  public metas!: Metas;
  public clientes: Clientes = [];
  public clienteId: number = Number(localStorage.getItem('clienteId'));
  public formularioMeta !: FormGroup;
  public placeholderClientes: string = 'COMPONENTS.METAS.form.plantPlaceholderLoading';
  public botaoDesabilitado: boolean = true;
  public loadingBotao: boolean = false;
  public trabalhaSeteDias: boolean = true;

  ngOnInit(): void {
    this.construirForm();
    this.buscarClientes();
    this.atualizarEstadoBotaoSubmit();
    this.verificaTrabalhaSeteDias(this.trabalhaSeteDias);
  }

  constructor(
    private formBuilder: FormBuilder,
    private clienteService: ClienteService,
    private metaService: MetaService,
    private dialogService: DialogService,
    private router: Router,
    private location: Location,
    private translocoService: TranslocoService,
  ) { }

  public construirForm(): void {
    this.formularioMeta = this.formBuilder.group({
      vc_nome: ['', Validators.required],
      fk_cliente: ['', Validators.required],
      dt_inicio_meta: ['', Validators.required],
      dt_fim_meta: ['', Validators.required],
      fl_meta_total: ['', Validators.required],
      it_dias_trabalhados_semana: ['7', [Validators.required, Validators.max(7), Validators.min(1)]]
    }, { validators: this.validacaoDatasInvalidas, updateOn: 'blur' });
  }

  public buscarClientes(): void {
    this.placeholderClientes = 'COMPONENTS.METAS.form.plantPlaceholderLoading';
    this.clienteService.buscarClientesUsuario()
      .subscribe(
        (clientes) => {
          this.clientes = clientes;
          this.formularioMeta.get('fk_cliente')?.setValue(this.clienteId);
          this.placeholderClientes = 'COMPONENTS.METAS.form.plantPlaceholder';
        });
  }

  public cadastrar(): void {
    this.loadingBotao = true;
    const metaData: Meta = this.formularioMeta.value;

    metaData.it_dias_trabalhados_semana = metaData.it_dias_trabalhados_semana ?? 7;

    this.metaService.cadastrar(metaData)
      .pipe(finalize(() => this.loadingBotao = false))
      .subscribe({
        next: () => {
          this.dialogService.sucesso(
            this.translocoService.translate('COMPONENTS.METAS.form.successTitle'),
            this.translocoService.translate('COMPONENTS.METAS.form.successMessage')
          ).then(() => {
            this.router.navigate(['/premoplan/metas']);
          });
        },
        error: (e) => {
          if (e.status === 409) {
            this.dialogService.erro(
              this.translocoService.translate('COMMON.error'),
              this.translocoService.translate('COMPONENTS.METAS.form.conflictError')
            );
            return;
          }

          this.dialogService.erro(
            this.translocoService.translate('COMPONENTS.METAS.form.errorTitle'),
            this.translocoService.translate('COMPONENTS.METAS.form.errorMessage')
          );
        }
      });
  }

  public onSubmit(): void {
    this.cadastrar();
  }

  public atualizarEstadoBotaoSubmit(): void {
    this.formularioMeta.valueChanges.subscribe(() => {
      this.botaoDesabilitado = this.formularioMeta.invalid || !this.formularioMeta.dirty;
    });
  }

  public validarCampoInvalido(campo: AbstractControl | null): boolean | undefined {
    return campo?.invalid && campo.dirty;
  }

  verificaTrabalhaSeteDias(trabalhaSeteDias: boolean): void {
    this.trabalhaSeteDias = trabalhaSeteDias;
    if (trabalhaSeteDias) {
      this.formularioMeta.controls['it_dias_trabalhados_semana'].setValue(7);
      this.formularioMeta.controls['it_dias_trabalhados_semana'].disable();
    } else {
      this.formularioMeta.controls['it_dias_trabalhados_semana'].setValue(null);
      this.formularioMeta.controls['it_dias_trabalhados_semana'].enable();
    }
  }

  validacaoDatasInvalidas: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const dataInicio = control.get('dt_inicio_meta')?.value;
    const dataFim = control.get('dt_fim_meta')?.value;
    return dataInicio && dataFim && dataInicio > dataFim ? { datasInvalidas: true } : null;
  }

  public cancelar(): void {
    this.router.navigate(['/premoplan/metas']);
  }
}
